import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Lock, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Shelby!");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-12"
      style={{ backgroundColor: "var(--bg-primary)" }}
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm mb-8 hover:underline"
          style={{ color: "var(--text-secondary)" }}
          data-testid="admin-back-to-site"
        >
          <ArrowLeft size={16} />
          back to site
        </Link>

        <div
          className="bg-white rounded-2xl p-8 md:p-10"
          style={{ border: "1px solid var(--border-soft)", boxShadow: "0 12px 30px -16px rgba(74,93,78,0.18)" }}
        >
          <div className="text-center mb-7">
            <p
              className="font-script text-2xl mb-1"
              style={{ color: "var(--text-accent)" }}
            >
              welcome back
            </p>
            <h1
              className="font-display text-4xl"
              style={{ color: "var(--text-primary)" }}
            >
              Studio Login
            </h1>
            <p
              className="text-sm mt-2"
              style={{ color: "var(--text-secondary)" }}
            >
              Manage your plushies, subscribers &amp; more.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="text-sm font-semibold block mb-1.5" style={{ color: "var(--text-primary)" }}>
                Email
              </label>
              <div className="relative">
                <Mail
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: "var(--text-secondary)" }}
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl outline-none"
                  style={{
                    backgroundColor: "var(--bg-secondary)",
                    border: "1px solid var(--border-soft)",
                    color: "var(--text-primary)",
                  }}
                  data-testid="admin-email-input"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold block mb-1.5" style={{ color: "var(--text-primary)" }}>
                Password
              </label>
              <div className="relative">
                <Lock
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: "var(--text-secondary)" }}
                />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl outline-none"
                  style={{
                    backgroundColor: "var(--bg-secondary)",
                    border: "1px solid var(--border-soft)",
                    color: "var(--text-primary)",
                  }}
                  data-testid="admin-password-input"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              className="btn-sage w-full py-3 rounded-full text-sm font-semibold tracking-wide uppercase disabled:opacity-60"
              data-testid="admin-login-submit"
            >
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
